import React from "react";
import isURL from "validator/lib/isURL";
import { connect } from "react-redux";
import _ from "lodash";
import * as resActions from "../../modules/resources";
import PropTypes from "prop-types";
import { alertSuccess } from "Utils/alerts";
import QboxmailApi from "../../qboxmailApi";
import Loader from "Components/loader/Loader";
import withFormState from "Components/withFormState";
import Input from "Components/input/Input";
import Button from "Components/button/Button";
import { convertFileToBase64 } from "Utils/file";
import { formatServerErrors } from "Utils/serverFormats";
import Dropzone from "react-dropzone";
import MultipleSelector from "Components/multipleSelector/MultipleSelector";
import WarningBanner from "Components/warningBanner/WarningBanner";
import TranslatedString from "Components/TranslatedString";
import t from "Utils/translation";
import CustomizationPreview from "Components/customizationPreview/CustomizationPreview";
import cx from "classnames";
import { Icon, upload, file } from "Icons/index";
import s from "./New.scss";
import sessionStore from "@/stores/session";

const LINKS_ILLUSTRATIONS = `${BASE_URL_PUBLIC_ASSETS}/customization_links_image.svg`;

const propTypes = {};

const ContextTypes = {
  router: PropTypes.object,
  isLoggedUserTrial: PropTypes.func,
  getLoggedUser: PropTypes.func,
  isLoggedUserAdmin: PropTypes.func,
};

class CustomizationEditScreen extends React.Component {
  constructor(props, context) {
    super(props, context);

    this.state = {
      isEditingMainCustomization: props.location.pathname.match("main"),
    };
  }

  componentDidMount() {
    const { getAllDomainsWithoutCustomizations, getCustomization, getMainCustomization } = this.props;
    const { isLoggedUserAdmin } = this.context;

    if (this.state.isEditingMainCustomization) {
      getMainCustomization();
    } else {
      getCustomization();
    }

    if (!isLoggedUserAdmin()) getAllDomainsWithoutCustomizations();
  }

  componentDidUpdate(prevProps) {
    const hasJustLoadedCustomization = !this.props.isCustomizationLoading && prevProps.isCustomizationLoading;

    if (hasJustLoadedCustomization) {
      this.setCustomizationValues();
    }
  }

  setCustomizationValues = () => {
    const customization = Object.assign({}, this.props.customization);
    let initData = {
      help_desk_url: customization.help_desk_url,
      recover_password_url: customization.recover_password_url,
      custom_url_1_label: customization.custom_url_1_label,
      custom_url_1_url: customization.custom_url_1_url,
      company: customization.company,
      domain_codes: customization.domain_codes,
    };

    if (this.context.isLoggedUserAdmin()) {
      initData.custom_url = customization.custom_url;
      initData.webmail_custom_url = customization.webmail_custom_url;
      initData.mtm_custom_url = customization.mtm_custom_url;
      initData.vault_custom_url = customization.vault_custom_url;
    }

    this.props.setInitialValues(initData);

    this.props.getAllDomainsWithoutCustomizations({
      customer_code: customization.customer_code,
    });
    this.setState({ imgUrl: customization.logo });
  };

  componentWillUnmount() {
    this.props.resetForm();
    this.props.resetSelectedResource("customization");
    this.props.resetSelectedResource("domainsNoCustomizations");
  }

  returnToList = () => {
    let url = "/customizations";

    if (!this.state.isEditingMainCustomization) {
      url += "#domains";
    }

    if (this.context.isLoggedUserAdmin()) {
      this.props.history.push(`/customers/${this.props.match.params.customerCode}${url}`);
    } else {
      this.props.history.push(url);
    }

    this.props.resetForm();
  };

  validate = (values) => {
    let errors = {};
    if (!this.state.isEditingMainCustomization && values.domain_codes.length === 0) {
      errors.domain_codes = t("errorMsg.select.domain");
    }

    if (values.help_desk_url && !isURL(values.help_desk_url)) {
      errors.help_desk_url = t("errorMsg.notValid.URL");
    }

    if (values.recover_password_url && !isURL(values.recover_password_url)) {
      errors.help_desk_url = t("errorMsg.notValid.URL");
    }

    if (values.custom_url_1_url && !isURL(values.custom_url_1_url)) {
      errors.custom_url_1_url = t("errorMsg.notValid.URL");
    }

    if (values.custom_url_1_url && !values.custom_url_1_label) {
      errors.custom_url_1_label = t("errorsMsg.required.customization.custom_label");
    }

    if (values.custom_url_1_label && !values.custom_url_1_url) {
      errors.custom_url_1_url = t("errorsMsg.required.customization.custom_label");
    }

    const emptyHelp = !values.help_desk_url || values.help_desk_url === "";
    const emptyCompany = !values.company || values.company === "";

    if (
      emptyHelp &&
      emptyCompany &&
      ((!this.state.imgBlob && !this.state.reset_logo && !this.props.customization.logo) || this.state.reset_logo)
    ) {
      errors.onForm = t("errorMsg.required.customization.field");
    }

    return errors;
  };

  submitForm = async (e) => {
    e.preventDefault();
    const {
      formData,
      formData: { file },
      setFormLoading,
      customization,
      refreshCustomizations,
      unsetFormLoading,
      setFormErrors,
      match: { params },
    } = this.props;
    const { router, isLoggedUserAdmin } = this.context;
    setFormLoading();
    const errors = this.validate(formData);

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      unsetFormLoading();
    } else {
      const api = new QboxmailApi({ useCookie: true });

      let fileMetaData;
      if (file) {
        // user has changed the logo
        fileMetaData = {
          logo: this.state.imgBlob,
          file_name: file.name,
        };
      }

      delete formData.file;

      const customId = this.state.isEditingMainCustomization ? "main" : customization.id;

      let dataToSend = Object.assign(formData, fileMetaData);

      if (this.state.reset_logo) {
        dataToSend.reset_logo = true;
      }

      if (isLoggedUserAdmin()) dataToSend.customer_code = params.customerCode;

      // remove empty string values before sending them to the server
      for (let i of ["help_desk_url", "recover_password_url", "custom_url_1_url", "custom_url_1_label"]) {
        if (dataToSend[i] === "") dataToSend[i] = null;
      }

      try {
        const resp = await api.editCustomization(customId, dataToSend);
        refreshCustomizations();
        alertSuccess(t("alert.customizationUpdatedSuccess"));

        if (customId === "main") {
          let url;
          if (resp.resources[0].logo) {
            url = `/customizations/${resp.resources[0].logo}`;
          } else {
            url = null;
          }

          if (!isLoggedUserAdmin()) {
            sessionStore.applyCustomization({
              logo: url,
              company: formData.company,
              help_desk_url: formData.help_desk_url,
            });
          }
        }

        this.returnToList();
      } catch (response) {
        formatServerErrors(response).then((errors) => {
          setFormErrors(errors);
          unsetFormLoading();
        });
      }
    }
  };

  onDrop = (files, reject) => {
    if (files.length > 1 || reject.length > 0) {
      window.alert(t("alert.invalidFileImageFormat"));
    } else {
      const file = files[0];
      this.props.setFormFieldValue({ file });
      convertFileToBase64(file).then((content) => {
        this.setState({
          file_content: content,
          file_name: file.name,
          file_size: file.size,
          imgBlob: content,
          reset_logo: false,
        });
      });
    }
  };

  resetAndGoBack = () => {
    this.props.resetForm();
    this.returnToList();
  };

  resetFileField = (e) => {
    e.preventDefault();
    this.props.setFormFieldValue({
      file: undefined,
      imgBlob: undefined,
      imgUrl: undefined,
    });
    this.setState({ imgUrl: undefined, imgBlob: undefined, reset_logo: true });
  };

  renderDropzone = () => {
    const acceptedMIMETypes = ["image/*"];
    return (
      <Dropzone onDrop={this.onDrop} accept={acceptedMIMETypes}>
        {(params) => {
          return (
            <div
              {...params.getRootProps()}
              className={cx("contentDZ", {
                active: params.isDragActive,
                reject: params.isDragReject,
              })}
            >
              <input {...params.getInputProps()} />
              <Icon className="iconDZ" svg={upload} width="1rem" height="1rem" />
              <div className="textDZ">{t("input.label.dragHere")}</div>
            </div>
          );
        }}
      </Dropzone>
    );
  };

  renderFilePreview = () => {
    const { formData, customization } = this.props;
    return (
      <div className={cx("filePrewDZ", "inputFull2")}>
        <div className="contentDZ">
          <Icon className="iconDZ" svg={file} width="1rem" height="1rem" />
          <div className="textDZ">
            {this.props.formData.file
              ? formData.file.name
              : customization.logo && customization.logo.replace("/customizations/", "")}
          </div>
        </div>
        <Button className="btnDZ" color="outline" onClick={this.resetFileField} sizeSmall>
          button.change
        </Button>
      </div>
    );
  };

  render() {
    const {
      formData,
      setFormFieldValue,
      formErrors,
      isFormLoading,
      customization,
      areDomainNoCustomizationsLoading,
      domainsNoCustomizations,
      isCustomizationLoading,
    } = this.props;

    if (isCustomizationLoading) {
      return <Loader />;
    }

    let companyName;
    if (formData.company?.length > 28) {
      companyName = formData.company.slice(0, 28) + "...";
    } else {
      companyName = formData.company;
    }

    const isLoggedUserAdmin = this.context.isLoggedUserAdmin();

    return (
      <div className="formsPage">
        <div className={cx("sectionTitle", "titleFP")}>
          {this.state.isEditingMainCustomization
            ? t("customizations.edit.general.title")
            : t("customizations.edit.domains.title")}
        </div>
        <div className="wrapScrollFP">
          <div className="infoFP">
            <div className="sectionDesc">
              {this.state.isAddingMainCustomization ? (
                <TranslatedString>customizations.add.general.desc</TranslatedString>
              ) : (
                <TranslatedString>customizations.add.domains.desc</TranslatedString>
              )}
            </div>
            <div className="wrapBtnFP">
              <Button
                className="btnDocsFP"
                docsKey={
                  this.state.isAddingMainCustomization ? "customizations_general_edit" : "customizations_domains_edit"
                }
                color="docs"
                sizeSmall
              >
                button.docs
              </Button>
            </div>
          </div>

          <form className="wrapFormsFP" method="post" onSubmit={this.submitForm}>
            <div className="formsFP">
              {!this.state.isEditingMainCustomization && !areDomainNoCustomizationsLoading && (
                <MultipleSelector
                  className={cx("inputFull2", "inputAddMargin")}
                  name="domain_codes"
                  label={t("input.label.customization.domains")}
                  options={_.map(domainsNoCustomizations, (v) => ({
                    value: v.code,
                    label: v.name,
                  }))}
                  selected={_.map(customization.selected_domain_data, (v) => ({
                    value: v.code,
                    label: v.name,
                  }))}
                  firstPanelTitle={t("input.label.domainsAvailable")}
                  secondPanelTitle={t("input.label.domainsSelected")}
                  onChange={setFormFieldValue}
                />
              )}

              <Input
                name="company"
                label={t("input.label.company")}
                placeholder={t("input.placeholder.company")}
                type="text"
                value={formData.company}
                errors={formErrors.company}
                onChange={setFormFieldValue}
                className="inputFull2"
                hint={t("input.hint.customization.company")}
                maxLength="255"
              />

              <div className={cx("wrapDropzone", "inputFull2", "inputAddMargin")}>
                <div className={s.wrapLabelDZ}>
                  <label className={s.labelDZ}>{t("input.label.customization.upload.logo")}</label>

                  <div className={s.hintDZ}>{t("input.hint.customization.upload.logo")}</div>
                </div>
                {!this.state.imgBlob && !this.state.imgUrl && this.renderDropzone()}
                {(this.state.imgBlob || this.state.imgUrl) && this.renderFilePreview()}
              </div>

              <div className={cx(s.wrapPreview, "inputFull2", "inputAddMargin")}>
                <label className={s.labelPreview}>{t("input.label.customization.preview")}</label>
                <CustomizationPreview
                  company={companyName}
                  image={
                    this.state.imgBlob || (this.state.imgUrl ? `${BASE_URL_PUBLIC_ASSETS}${this.state.imgUrl}` : null)
                  }
                />
              </div>

              {isLoggedUserAdmin && (
                <>
                  <Input
                    className="inputFull2 inputAddMargin"
                    name="help_desk_url"
                    label={t("input.label.customization.helpDeskUrl")}
                    placeholder={t("input.placeholder.url")}
                    type="url"
                    value={formData.help_desk_url}
                    errors={formErrors.help_desk_url}
                    onChange={setFormFieldValue}
                    hint={t("input.hint.customization.helpDeskUrl")}
                  />

                  <Input
                    className="inputFull2 inputAddMargin"
                    name="recover_password_url"
                    type="url"
                    label={t("input.label.customization.recoverPasswordUrl1")}
                    placeholder={t("input.placeholder.url")}
                    hint={t("input.hint.customization.recoverPasswordUrl1")}
                    value={formData.recover_password_url}
                    errors={formErrors.recover_password_url}
                    onChange={setFormFieldValue}
                  />

                  <Input
                    className="inputFull2"
                    name="custom_url_1_label"
                    label={t("input.label.customization.customUrl1")}
                    placeholder={t("input.placeholder.helpDeskUrl.label")}
                    hint={t("input.hint.customization.customUrl1")}
                    value={formData.custom_url_1_label}
                    errors={formErrors.custom_url_1_label}
                    onChange={setFormFieldValue}
                  />

                  <Input
                    className="inputFull2 pt10-sm inputAddMargin"
                    type="url"
                    name="custom_url_1_url"
                    noLabel
                    placeholder={t("input.placeholder.url")}
                    value={formData.custom_url_1_url}
                    errors={formErrors.custom_url_1_url}
                    onChange={setFormFieldValue}
                  />

                  <img className={cx("inputFull2", s.linksImg)} src={LINKS_ILLUSTRATIONS} />

                  <Input
                    className="inputFull2"
                    name="custom_url"
                    label={t("input.label.customization.custom_url")}
                    placeholder={"panel.mycustomurl.com"}
                    value={formData.custom_url}
                    errors={formErrors.custom_url}
                    onChange={setFormFieldValue}
                    blockedTextStart="https://"
                  />

                  <Input
                    className="inputFull2"
                    name="webmail_custom_url"
                    label={t("input.label.customization.custom_url.webmail")}
                    value={formData.webmail_custom_url}
                    errors={formErrors.webmail_custom_url}
                    onChange={setFormFieldValue}
                    blockedTextStart="https://"
                    placeholder={"webmail.mycustomurl.com"}
                  />

                  <Input
                    className="inputFull2"
                    name="mtm_custom_url"
                    label={t("input.label.customization.custom_url.mtm")}
                    value={formData.mtm_custom_url}
                    errors={formErrors.mtm_custom_url}
                    onChange={setFormFieldValue}
                    blockedTextStart="https://"
                    placeholder={"mtm.mycustomurl.com"}
                  />

                  <Input
                    className="inputFull2"
                    name="vault_custom_url"
                    label={t("input.label.customization.custom_url.vault")}
                    placeholder={"vault.mycustomurl.com"}
                    value={formData.vault_custom_url}
                    errors={formErrors.vault_custom_url}
                    onChange={setFormFieldValue}
                    blockedTextStart="https://"
                  />
                </>
              )}
            </div>

            {(!this.props.areFieldsPristine("domain_codes", "company", "help_desk_url") || this.state.imgBlob) &&
              !formErrors.onForm && (
                <div className="warningMsgFP">
                  <WarningBanner msg={<TranslatedString>infoMsg.unsaveChanged</TranslatedString>} />
                </div>
              )}

            {formErrors.onForm && (
              <div className="warningMsgFP">
                <WarningBanner msg={formErrors.onForm} color="red" />
              </div>
            )}

            <div className="footerFP">
              <Button color="text" type="button" onClick={this.resetAndGoBack}>
                button.cancel
              </Button>
              <Button color="accent" type="submit" loading={isFormLoading}>
                button.save
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  }
}
//CustomizationEditScreen.propTypes = propTypes;
CustomizationEditScreen.contextTypes = ContextTypes;
const mapStateToProps = (state) => ({
  customization: resActions.getData("customization.data.0", state),
  isCustomizationLoading: resActions.isResourceLoading("customization", state),
  domainsNoCustomizations: resActions.getData("domainsNoCustomizations.data", state),
  areDomainNoCustomizationsLoading: resActions.isResourceLoading("domainsNoCustomizations", state),
});
const mapDispatchToProps = (dispatch, props) => {
  const customizationId = props.match.params.customizationId;
  const customerCode = props.match.params.customerCode;

  return {
    getMainCustomization: () =>
      dispatch(resActions.callApi("getMainCustomization", "customization", { customer_code: customerCode })),
    getCustomization: () => dispatch(resActions.callApi("getCustomization", "customization", customizationId)),
    getAllDomainsWithoutCustomizations: (params) =>
      dispatch(resActions.callApi("getAllDomainsWithoutCustomizations", "domainsNoCustomizations", params)),
    resetSelectedResource: (resourcesType) => dispatch(resActions.resetSelectedResource(resourcesType)),
    refreshCustomizations: (queryParams) =>
      dispatch(
        resActions.callApi("refreshCustomizations", "customizations", {
          ...queryParams,
          customer_code: customerCode,
        })
      ),
  };
};
export default connect(mapStateToProps, mapDispatchToProps)(withFormState(CustomizationEditScreen, {}));
